import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getMangaById, categoryMap } from '@/mocks/manga';

function formatViews(views: number): string {
  if (views >= 10000) {
    return (views / 10000).toFixed(1) + '万';
  }
  return views.toString();
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const manga = getMangaById(Number(id));

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，该漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full text-sm transition-all duration-300 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 香香漫画免费在线阅读`,
    description: manga.description,
    author: {
      '@type': 'Person',
      name: manga.author,
    },
    datePublished: manga.updatedAt,
    dateModified: manga.updatedAt,
    url: `https://www.xiangxiangmh.com/article/${manga.id}`,
    publisher: {
      '@type': 'Organization',
      name: '香香漫画',
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryMap[manga.category], href: `/category/${manga.category}` },
    { label: manga.title },
  ];

  return (
    <>
      <SEO
        title={`${manga.title} - 香香漫画 | 免费在线阅读`}
        description={manga.description.slice(0, 150)}
        keywords={`香香漫画,${manga.title},${categoryMap[manga.category]},漫画在线阅读,免费漫画`}
        canonicalUrl={`https://www.xiangxiangmh.com/article/${manga.id}`}
        ogType="article"
        ogImage={manga.cover}
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row gap-8 py-8">
            <div className="w-full md:w-[300px] lg:w-[350px] flex-shrink-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                <img
                  src={manga.cover}
                  alt={`${manga.title} 漫画封面`}
                  title={`${manga.title} - 香香漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                {manga.title}
              </h1>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                  {categoryMap[manga.category]}
                </span>
                {manga.status === '连载中' ? (
                  <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                    连载中
                  </span>
                ) : (
                  <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    已完结
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                <span>
                  <strong className="text-foreground-700">作者：</strong>{manga.author}
                </span>
                <span>
                  <strong className="text-foreground-700">评分：</strong>
                  <span className="text-accent-500 font-semibold">{manga.rating}</span>
                </span>
                <span>
                  <strong className="text-foreground-700">阅读：</strong>{formatViews(manga.views)}
                </span>
                <span>
                  <strong className="text-foreground-700">更新：</strong>{manga.updatedAt}
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-6">
                {manga.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-50 rounded-xl p-4 md:p-5 mb-6">
                <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {manga.description}
                </p>
              </div>

              <Link
                to={`/article/${manga.id}#chapters`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('chapters')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full text-sm transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-play-circle-line"></i>
                开始阅读
              </Link>
            </div>
          </div>

          <section id="chapters" className="py-8 border-t border-background-200/70">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-heading text-foreground-900">
                章节目录
              </h2>
              <span className="text-sm text-foreground-400">
                共 {manga.chapters.length} 话
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {manga.chapters.map((chapter) => (
                <a
                  key={chapter.id}
                  href="#"
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors duration-200 group"
                >
                  <span className="text-sm text-foreground-700 font-medium group-hover:text-primary-500 transition-colors duration-200">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
                </a>
              ))}
            </div>
          </section>

          <section className="py-8 border-t border-background-200/70">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-heading text-foreground-900">
                相关推荐
              </h2>
              <Link
                to={`/category/${manga.category}`}
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}